import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useAuth } from '@clerk/clerk-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, useEffect, useMemo } from 'react';
import { track } from '@/lib/analytics';
import { FiArrowLeft } from 'react-icons/fi';
import { Card, Button } from '@/components';
import { apiJson, ApiError } from '@/lib/api';
import { useHouseholds } from '@/lib/household-context';
import { formatDateLocal } from '@/lib/utils';
import type {
  UserResponse,
  HouseholdResponse,
  BeneficiaryResponse,
  ScheduleType,
} from '@/lib/types';
import {
  SCHEDULE_OPTIONS,
  SCHEDULE_PATTERNS,
  getTransitionPoints,
  getSegments,
} from '@/lib/types';

const SECTION_HEADING_STYLE = {
  fontFamily: "'Nunito', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
} as const;

const DOW_HEADERS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const PARENT_COLORS: Record<number, string> = {
  0: 'bg-primary',
  1: 'bg-amber-500',
};

const PARENT_HEX: Record<number, string> = {
  0: '#018B9C',
  1: '#E8913A',
};

function AddSchedulePage() {
  const { getToken } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  useEffect(() => {
    track('Add Custody Schedule Screen');
  }, []);

  const [selectedBeneficiary, setSelectedBeneficiary] = useState<number | null>(null);
  const [selectedScheduleType, setSelectedScheduleType] = useState<ScheduleType>('ALTERNATING_WEEKS');
  const [selectedFirstParent, setSelectedFirstParent] = useState<number | null>(null);
  const [startDate, setStartDate] = useState(formatDateLocal(new Date()));

  const [transitionTimes, setTransitionTimes] = useState<
    { time: string; onFirstDay: boolean }[]
  >([]);
  const [useCustomTimes, setUseCustomTimes] = useState(false);
  const [globalTime, setGlobalTime] = useState('');

  const userQuery = useQuery({
    queryKey: ['user'],
    queryFn: () => apiJson<UserResponse>(getToken, '/v1/user'),
  });

  const householdsQuery = useQuery({
    queryKey: ['households'],
    queryFn: async () => {
      try {
        return await apiJson<HouseholdResponse[]>(getToken, '/v1/households');
      } catch (err) {
        if (err instanceof ApiError && (err.status === 403 || err.status === 404)) {
          return [] as HouseholdResponse[];
        }
        throw err;
      }
    },
  });

  const currentUser = userQuery.data;
  const { currentHouseholdId } = useHouseholds();
  const households = householdsQuery.data ?? [];
  const firstHousehold =
    households.find((h) => h.id === currentHouseholdId) ?? households[0];

  const children: BeneficiaryResponse[] = (firstHousehold?.beneficiaries ?? []).filter(
    (b) => b.type === 'CHILD'
  );

  const parents = firstHousehold?.members ?? [];

  const getParentName = (userId: number): string => {
    const member = parents.find((m) => m.user_id === userId);
    if (!member) return `Parent ${userId}`;
    if (member.first_name) return member.first_name;
    return `Parent ${userId}`;
  };

  const pattern = SCHEDULE_PATTERNS[selectedScheduleType] as number[] | undefined;
  const transitions = useMemo(() => (pattern ? getTransitionPoints(pattern) : []), [pattern]);
  const segments = useMemo(() => (pattern ? getSegments(pattern) : []), [pattern]);

  // Re-init transition times when schedule type changes
  useEffect(() => {
    if (!pattern) {
      setTransitionTimes([]);
      return;
    }
    const points = getTransitionPoints(pattern);
    setTransitionTimes(
      points.map((tp) => ({ time: '', onFirstDay: tp.fromParent === 0 }))
    );
    setUseCustomTimes(false);
    setGlobalTime('');
  }, [selectedScheduleType]); // eslint-disable-line react-hooks/exhaustive-deps

  const resolvedFirstId = selectedFirstParent ?? parents[0]?.user_id;
  const firstParentName = (() => {
    const p = resolvedFirstId ? parents.find((m) => m.user_id === resolvedFirstId) : null;
    if (!p) return 'Parent A';
    const name = p.first_name || `Parent ${p.user_id}`;
    return p.user_id === currentUser?.id ? `${name} (You)` : name;
  })();
  const secondParentName = (() => {
    const p = parents.find((m) => m.user_id !== resolvedFirstId);
    if (!p) return 'Parent B';
    const name = p.first_name || `Parent ${p.user_id}`;
    return p.user_id === currentUser?.id ? `${name} (You)` : name;
  })();

  const getInitial = (name: string) => name.replace(/\s*\(You\)\s*$/, '').charAt(0).toUpperCase();
  const getShortName = (name: string) => name.replace(/\s*\(You\)\s*$/, '').trim();

  const handleSwitchToCustom = () => {
    if (globalTime) {
      setTransitionTimes(transitionTimes.map((t) => ({ ...t, time: globalTime })));
    }
    setUseCustomTimes(true);
  };

  const handleSwitchToSimple = () => {
    const firstTime = transitionTimes.find((t) => t.time)?.time || '';
    setGlobalTime(firstTime);
    setUseCustomTimes(false);
  };

  const handleApplyTimeToAll = (sourceIdx: number) => {
    const source = transitionTimes[sourceIdx];
    if (!source?.time) return;
    setTransitionTimes(transitionTimes.map((t) => ({ ...t, time: source.time })));
  };

  const createScheduleMutation = useMutation({
    mutationFn: async () => {
      if (!selectedBeneficiary || !selectedFirstParent) {
        throw new Error('Please fill in all required fields.');
      }

      // Compute effective transition times based on mode
      let effectiveTimes = transitionTimes;
      if (!useCustomTimes && globalTime && pattern) {
        const points = getTransitionPoints(pattern);
        effectiveTimes = points.map((tp) => ({
          time: globalTime,
          onFirstDay: tp.fromParent === 0,
        }));
      }

      const body: Record<string, unknown> = {
        schedule_type: selectedScheduleType,
        start_date: startDate,
        first_parent_user_id: selectedFirstParent,
      };

      const hasAnyTime = effectiveTimes.some((t) => t.time);
      if (hasAnyTime) {
        body.transition_times = effectiveTimes.map((t) => ({
          time: t.time ? `${t.time}:00` : null,
          on_first_day: t.onFirstDay,
        }));
      }

      return apiJson(getToken, `/v1/beneficiaries/${selectedBeneficiary}/schedule`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['custody-schedules'] });
      await queryClient.invalidateQueries({ queryKey: ['calendar'] });
      navigate({ to: '/app/calendar' });
    },
    onError: (err: unknown) => {
      const errorMessage = err instanceof Error ? err.message : 'Could not create custody schedule.';
      alert(errorMessage);
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedBeneficiary) {
      alert('Please select a child.');
      return;
    }
    if (!selectedFirstParent) {
      alert('Please select who starts the schedule.');
      return;
    }
    createScheduleMutation.mutate();
  };

  const isLoading = householdsQuery.isLoading || userQuery.isLoading;

  if (isLoading) {
    return (
      <div className='min-h-screen bg-sky-light flex items-center justify-center'>
        <div className='animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full' />
      </div>
    );
  }

  if (children.length === 0) {
    return (
      <div className='min-h-screen bg-sky-light'>
        <header className='bg-sky-light border-b border-gray-200 sticky top-0 z-10'>
          <div className='max-w-lg lg:max-w-2xl mx-auto px-6 lg:px-8 py-4'>
            <Link to='/app/calendar' className='inline-flex items-center gap-2 text-primary hover:underline'>
              <FiArrowLeft /> Back to Calendar
            </Link>
          </div>
        </header>
        <div className='max-w-lg lg:max-w-2xl mx-auto px-6 lg:px-8 py-8'>
          <Card className='text-center py-8'>
            <h3 className='font-bold text-gray-900 mb-2' style={SECTION_HEADING_STYLE}>
              No Children Added
            </h3>
            <p className='text-gray-500'>
              Please add children to your household first before setting up a custody schedule.
            </p>
          </Card>
        </div>
      </div>
    );
  }

  // Build the calendar grid preview for custom mode
  const renderPatternPreview = () => {
    if (!pattern) return null;
    const startDow = new Date(startDate + 'T00:00:00').getDay();
    const totalCells = startDow + pattern.length;
    const totalRows = Math.ceil(totalCells / 7);

    const transitionDayMap = new Map<number, number>();
    transitions.forEach((tp, idx) => {
      const tt = transitionTimes[idx] || { onFirstDay: true };
      const day = tt.onFirstDay ? tp.nextIndex : tp.index;
      transitionDayMap.set(day, idx + 1);
    });

    return (
      <div className='bg-white rounded-xl border border-gray-200 p-4 mb-5'>
        <p className='text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3'>
          Schedule Preview — {pattern.length} day cycle
        </p>
        {/* Legend */}
        <div className='flex gap-4 mb-3'>
          <div className='flex items-center gap-1.5'>
            <span className='w-2.5 h-2.5 rounded-full bg-primary' />
            <span className='text-xs text-gray-600'>{firstParentName}</span>
          </div>
          <div className='flex items-center gap-1.5'>
            <span className='w-2.5 h-2.5 rounded-full bg-amber-500' />
            <span className='text-xs text-gray-600'>{secondParentName}</span>
          </div>
        </div>
        {/* Day-of-week headers */}
        <div className='grid grid-cols-7 gap-1 mb-1'>
          {DOW_HEADERS.map((d) => (
            <div key={d} className='text-center text-[11px] font-semibold text-gray-400 py-1'>
              {d}
            </div>
          ))}
        </div>
        {/* Day grid */}
        <div className='grid grid-cols-7 gap-1'>
          {Array.from({ length: totalRows * 7 }).map((_, cellIndex) => {
            const patternDay = cellIndex - startDow;
            if (patternDay < 0 || patternDay >= pattern.length) {
              return <div key={cellIndex} className='aspect-square' />;
            }
            const parentIdx = pattern[patternDay];
            const parentName = parentIdx === 0 ? firstParentName : secondParentName;
            const initial = getInitial(parentName);
            const transitionNum = transitionDayMap.get(patternDay);

            return (
              <div
                key={cellIndex}
                className={`aspect-square rounded-md flex flex-col items-center justify-center relative ${PARENT_COLORS[parentIdx]}`}
              >
                <span className='text-[11px] font-bold text-white'>{initial}</span>
                <span className='text-[8px] text-white/70 mt-0.5'>{patternDay + 1}</span>
                {transitionNum != null && (
                  <span className='absolute top-0.5 right-0.5 w-3.5 h-3.5 rounded-full bg-gray-700 border border-white text-[7px] font-bold text-white flex items-center justify-center'>
                    {transitionNum}
                  </span>
                )}
              </div>
            );
          })}
        </div>
        <p className='text-xs text-gray-400 italic text-center mt-2.5'>
          Pattern repeats after day {pattern.length}
        </p>
      </div>
    );
  };

  const renderHandoverTimeSection = () => {
    if (!pattern || transitions.length === 0) return null;

    // Simple mode
    if (!useCustomTimes) {
      return (
        <div>
          <h2 className='text-lg font-bold text-gray-900 mb-2' style={SECTION_HEADING_STYLE}>
            Handover Time (Optional)
          </h2>
          <p className='text-sm text-gray-500 mb-3'>
            What time do custody exchanges happen?
          </p>
          <div className='flex items-center gap-3'>
            <input
              type='time'
              value={globalTime}
              onChange={(e) => setGlobalTime(e.target.value)}
              className='flex-1 px-4 py-3 rounded-xl bg-white border border-gray-200 text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all'
            />
            {globalTime && (
              <button
                type='button'
                onClick={() => setGlobalTime('')}
                className='px-3 py-2 text-red-600 hover:text-red-700 font-semibold'
              >
                Clear
              </button>
            )}
          </div>
          {transitions.length > 1 && (
            <button
              type='button'
              onClick={handleSwitchToCustom}
              className='mt-4 w-full text-left p-3.5 rounded-xl bg-teal-50 hover:bg-teal-100 transition-colors'
            >
              <p className='text-[13px] text-gray-500 mb-1'>
                Need different times for specific handovers?
              </p>
              <p className='text-sm font-semibold text-primary'>
                Customize per transition &rarr;
              </p>
            </button>
          )}
        </div>
      );
    }

    // Custom mode
    return (
      <div>
        <h2 className='text-lg font-bold text-gray-900 mb-2' style={SECTION_HEADING_STYLE}>
          Handover Times (Optional)
        </h2>
        <p className='text-sm text-gray-500 mb-3'>
          Customize the time for each custody handover.
        </p>
        <button
          type='button'
          onClick={handleSwitchToSimple}
          className='text-sm font-semibold text-primary mb-4 hover:underline'
        >
          &larr; Same time for all
        </button>

        {renderPatternPreview()}

        {/* Transition cards */}
        <div className='space-y-4'>
          {transitions.map((_tp, idx) => {
            const tt = transitionTimes[idx] || { time: '', onFirstDay: true };
            const fromSeg = segments[idx];
            const toSeg = segments[(idx + 1) % segments.length];
            const fromName = fromSeg.parent === 0 ? firstParentName : secondParentName;
            const toName = toSeg.parent === 0 ? firstParentName : secondParentName;
            const fromShort = getShortName(fromName);
            const toShort = getShortName(toName);

            return (
              <div key={idx} className='bg-white rounded-xl border border-gray-200 p-4'>
                {/* Header */}
                <div className='flex items-start gap-2.5 mb-3'>
                  <span className='w-6 h-6 rounded-full bg-gray-700 text-white text-xs font-bold flex items-center justify-center shrink-0 mt-0.5'>
                    {idx + 1}
                  </span>
                  <div>
                    <p className='text-sm font-semibold text-gray-900'>
                      After{' '}
                      <span style={{ color: PARENT_HEX[fromSeg.parent] }} className='font-bold'>
                        {fromShort}
                      </span>
                      &apos;s {fromSeg.length} {fromSeg.length === 1 ? 'day' : 'days'}
                    </p>
                    <p className='text-[13px] text-gray-500 mt-0.5'>
                      &rarr;{' '}
                      <span style={{ color: PARENT_HEX[toSeg.parent] }} className='font-semibold'>
                        {toShort}
                      </span>{' '}
                      gets {toSeg.length} {toSeg.length === 1 ? 'day' : 'days'}
                    </p>
                  </div>
                </div>

                {/* Time input */}
                <div className='flex items-center gap-3 mb-3'>
                  <input
                    type='time'
                    value={tt.time}
                    onChange={(e) => {
                      const updated = [...transitionTimes];
                      updated[idx] = { ...updated[idx], time: e.target.value };
                      setTransitionTimes(updated);
                    }}
                    className='flex-1 px-4 py-3 rounded-xl bg-gray-50 border border-gray-200 text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all'
                  />
                  {tt.time && (
                    <button
                      type='button'
                      onClick={() => {
                        const updated = [...transitionTimes];
                        updated[idx] = { ...updated[idx], time: '' };
                        setTransitionTimes(updated);
                      }}
                      className='px-3 py-2 text-red-600 hover:text-red-700 font-semibold text-sm'
                    >
                      Clear
                    </button>
                  )}
                </div>

                {/* "Show on" toggle */}
                <div className='flex items-center gap-2'>
                  <span className='text-[13px] text-gray-500 mr-1'>Show on:</span>
                  <button
                    type='button'
                    onClick={() => {
                      const updated = [...transitionTimes];
                      updated[idx] = { ...updated[idx], onFirstDay: true };
                      setTransitionTimes(updated);
                    }}
                    className={`px-3 py-1.5 rounded-lg text-[13px] font-medium border transition-colors ${
                      tt.onFirstDay
                        ? 'bg-primary text-white border-primary'
                        : 'bg-gray-100 text-gray-600 border-gray-200 hover:border-primary'
                    }`}
                  >
                    {toShort}&apos;s 1st day
                  </button>
                  <button
                    type='button'
                    onClick={() => {
                      const updated = [...transitionTimes];
                      updated[idx] = { ...updated[idx], onFirstDay: false };
                      setTransitionTimes(updated);
                    }}
                    className={`px-3 py-1.5 rounded-lg text-[13px] font-medium border transition-colors ${
                      !tt.onFirstDay
                        ? 'bg-primary text-white border-primary'
                        : 'bg-gray-100 text-gray-600 border-gray-200 hover:border-primary'
                    }`}
                  >
                    {fromShort}&apos;s last day
                  </button>
                </div>

                {/* Apply to all */}
                {tt.time && transitions.length > 1 && (
                  <div className='mt-3 text-right'>
                    <button
                      type='button'
                      onClick={() => handleApplyTimeToAll(idx)}
                      className='text-[13px] font-semibold text-primary hover:underline'
                    >
                      Apply time to all transitions
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className='min-h-screen bg-sky-light'>
      <header className='bg-sky-light border-b border-gray-200 sticky top-0 z-10'>
        <div className='max-w-lg lg:max-w-2xl mx-auto px-6 lg:px-8 py-4'>
          <Link to='/app/calendar' className='inline-flex items-center gap-2 text-primary hover:underline'>
            <FiArrowLeft /> Back to Calendar
          </Link>
        </div>
      </header>

      <div className='max-w-lg lg:max-w-2xl mx-auto px-6 lg:px-8 py-6'>
        <h1 className='text-2xl font-bold text-gray-900 mb-6' style={SECTION_HEADING_STYLE}>
          Add Custody Schedule
        </h1>

        <form onSubmit={handleSubmit} className='space-y-6'>
          {/* Select Child */}
          <div>
            <h2 className='text-lg font-bold text-gray-900 mb-3' style={SECTION_HEADING_STYLE}>
              Select Child
            </h2>
            <div className='flex flex-wrap gap-2'>
              {children.map((child) => (
                <button
                  key={child.beneficiary_id}
                  type='button'
                  onClick={() => setSelectedBeneficiary(child.beneficiary_id)}
                  className={`px-4 py-3 rounded-xl border transition-colors font-semibold ${
                    selectedBeneficiary === child.beneficiary_id
                      ? 'bg-primary text-white border-primary'
                      : 'bg-white text-gray-900 border-gray-200 hover:border-primary'
                  }`}
                >
                  {child.name}
                </button>
              ))}
            </div>
          </div>

          {/* Schedule Type */}
          <div>
            <h2 className='text-lg font-bold text-gray-900 mb-3' style={SECTION_HEADING_STYLE}>
              Schedule Type
            </h2>
            <div className='space-y-2'>
              {SCHEDULE_OPTIONS.map((option) => (
                <button
                  key={option.value}
                  type='button'
                  onClick={() => setSelectedScheduleType(option.value)}
                  className={`w-full text-left p-4 rounded-xl border transition-colors ${
                    selectedScheduleType === option.value
                      ? 'bg-primary text-white border-primary'
                      : 'bg-white text-gray-900 border-gray-200 hover:border-primary'
                  }`}
                >
                  <p className='font-bold'>{option.label}</p>
                  <p
                    className={`text-sm mt-1 ${
                      selectedScheduleType === option.value ? 'text-white/80' : 'text-gray-500'
                    }`}
                  >
                    {option.description}
                  </p>
                </button>
              ))}
            </div>
          </div>

          {/* Who starts */}
          <div>
            <h2 className='text-lg font-bold text-gray-900 mb-2' style={SECTION_HEADING_STYLE}>
              Who starts the schedule?
            </h2>
            <p className='text-sm text-gray-500 mb-3'>
              This parent will have the child on day 1 of the pattern.
            </p>
            <div className='flex flex-wrap gap-2'>
              {parents.map((parent) => (
                <button
                  key={parent.user_id}
                  type='button'
                  onClick={() => setSelectedFirstParent(parent.user_id)}
                  className={`px-4 py-3 rounded-xl border transition-colors font-semibold ${
                    selectedFirstParent === parent.user_id
                      ? 'bg-primary text-white border-primary'
                      : 'bg-white text-gray-900 border-gray-200 hover:border-primary'
                  }`}
                >
                  {getParentName(parent.user_id)}
                  {parent.user_id === currentUser?.id && ' (You)'}
                </button>
              ))}
            </div>
          </div>

          {/* Start Date */}
          <div>
            <h2 className='text-lg font-bold text-gray-900 mb-2' style={SECTION_HEADING_STYLE}>
              Start Date
            </h2>
            <p className='text-sm text-gray-500 mb-3'>When does this custody schedule begin?</p>
            <input
              type='date'
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className='w-full px-4 py-3 rounded-xl bg-white border border-gray-200 text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all'
            />
          </div>

          {/* Handover Times */}
          {renderHandoverTimeSection()}

          {/* Submit */}
          <Button
            type='submit'
            fullWidth
            disabled={
              createScheduleMutation.isPending ||
              !selectedBeneficiary ||
              !selectedFirstParent
            }
            loading={createScheduleMutation.isPending}
          >
            Create Schedule
          </Button>
        </form>
      </div>
    </div>
  );
}

export const Route = createFileRoute('/app/calendar/add-schedule')({
  component: AddSchedulePage,
});
